import { PageContainer } from '@/components/Atoms'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/Card'
import { CrossLinkTile } from '@/components/CrossLinkTile'
import usePermissions from '@/hooks/permissions/usePermissions'
import {
  soxRolePermissionsReportQueryOptions,
  soxUsersReportQueryOptions,
} from '@/hooks/queries/useSoxCompliance'
import { downloadFile } from '@/lib/utils/exports'
import { queryClient } from '@/main'
import { ScheduledReport } from '@repo/backend/sox-compliance'
import { Button } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { Calendar, ClipboardList, ShieldCheck, ShoppingCart, Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { AuditTrailsModal } from './-components/AuditTrailsModal'
import { OrdersReportModal } from './-components/OrdersReportModal'
import { ScheduledReportsList } from './-components/ScheduledReportsList'
import { ScheduleReportModal } from './-components/ScheduleReportModal'

export const Route = createFileRoute('/_auth/_modules/_app/sox-compliance/')({
  component: Compliance,
})

function Compliance() {
  const { t } = useTranslation('soxCompliance')
  const { canUser } = usePermissions()
  const [isAuditTrailsModalOpen, setIsAuditTrailsModalOpen] = useState(false)
  const [isOrdersReportModalOpen, setIsOrdersReportModalOpen] = useState(false)
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false)
  const [editingReport, setEditingReport] = useState<ScheduledReport | null>(null)

  const canCreate = canUser('sox_compliance_scheduled_reports_create')

  const getUsersReport = () => queryClient.ensureQueryData(soxUsersReportQueryOptions)
  const getRolePermissionsReport = () =>
    queryClient.ensureQueryData(soxRolePermissionsReportQueryOptions)

  const handleExportCSV = async (
    getCsvExport: () => Promise<{
      blob: Blob
      filename: string
    }>,
  ) => {
    try {
      const { filename, blob } = await getCsvExport()
      downloadFile(filename, blob)
    } catch (err) {
      console.error(err)

      toast.error(t('export.error'), {
        description: t('export.errorMessage'),
      })
    }
  }

  const handleEditReport = (report: ScheduledReport) => {
    setEditingReport(report)
    setIsScheduleModalOpen(true)
  }

  const handleCloseScheduleModal = () => {
    setIsScheduleModalOpen(false)
    setEditingReport(null)
  }

  return (
    <PageContainer>
      <Card>
        <CardHeader>
          <CardTitle>{t('reportDownloads.title')}</CardTitle>
        </CardHeader>

        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <CrossLinkTile
              icon={<Users />}
              title={t('reportDownloads.users.title')}
              linkText={t('reportDownloads.users.linkText')}
              onClick={() => handleExportCSV(getUsersReport)}
            />
            <CrossLinkTile
              icon={<ShieldCheck />}
              title={t('reportDownloads.rolePermissions.title')}
              linkText={t('reportDownloads.rolePermissions.linkText')}
              onClick={() => handleExportCSV(getRolePermissionsReport)}
            />
            <CrossLinkTile
              icon={<ClipboardList />}
              title={t('reportDownloads.auditTrails.title')}
              linkText={t('reportDownloads.auditTrails.linkText')}
              onClick={() => setIsAuditTrailsModalOpen(true)}
            />
            <CrossLinkTile
              icon={<ShoppingCart />}
              title={t('reportDownloads.orders.title')}
              linkText={t('reportDownloads.orders.linkText')}
              onClick={() => setIsOrdersReportModalOpen(true)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t('scheduledReports.title')}</CardTitle>
          {canCreate && (
            <Button onClick={() => setIsScheduleModalOpen(true)}>
              <Calendar className="mr-2 h-4 w-4" />
              {t('scheduledReports.scheduleNew')}
            </Button>
          )}
        </CardHeader>

        <CardContent>
          <ScheduledReportsList onEdit={handleEditReport} />
        </CardContent>
      </Card>

      <AuditTrailsModal isOpen={isAuditTrailsModalOpen} setIsOpen={setIsAuditTrailsModalOpen} />
      <OrdersReportModal isOpen={isOrdersReportModalOpen} setIsOpen={setIsOrdersReportModalOpen} />

      <ScheduleReportModal
        isOpen={isScheduleModalOpen}
        setIsOpen={setIsScheduleModalOpen}
        editingReport={editingReport}
        onClose={handleCloseScheduleModal}
      />
    </PageContainer>
  )
}
